// Ubicación de un negocio de Ágora para la ficha /negocios/{usuario}: provincia y ciudad.
//
// `vendor.state` no es confiable: muchos negocios lo tienen en «Buenos Aires» por defecto aunque
// atiendan en otra provincia (p. ej. un local de Alta Gracia, Córdoba, con `state = 'Buenos Aires'`).
// Lo que sí escribe el negocio es la dirección de sus servicios (autocompletada por Google), así que
// la provincia sale primero de ahí y `state` queda como último recurso.

// Nombres tal como los usa el directorio (/directorio/provincia/{slug}, /{rubro}/{provincia}).
const CABA = 'Ciudad Autónoma de Buenos Aires';
const TDF = 'Tierra del Fuego, Antártida e Islas del Atlántico Sur';

const REGIONS = {
  Argentina: [
    'Buenos Aires', CABA, 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos', 'Formosa',
    'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan',
    'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', TDF, 'Tucumán'
  ],
  Uruguay: [
    'Artigas', 'Canelones', 'Cerro Largo', 'Colonia', 'Durazno', 'Flores', 'Florida', 'Lavalleja',
    'Maldonado', 'Montevideo', 'Paysandú', 'Río Negro', 'Rivera', 'Rocha', 'Salto', 'San José', 'Soriano',
    'Tacuarembó', 'Treinta y Tres'
  ]
};

// Cómo aparecen en las direcciones de Google o en lo que cargan los negocios.
const ALIASES = {
  Argentina: {
    caba: CABA,
    'capital federal': CABA,
    'ciudad de buenos aires': CABA,
    'cdad autonoma de buenos aires': CABA,
    'autonomous city of buenos aires': CABA,
    'provincia de buenos aires': 'Buenos Aires',
    'buenos aires province': 'Buenos Aires',
    'tierra del fuego': TDF
  },
  Uruguay: {}
};

const COUNTRY_NAMES = new Set(['argentina', 'uruguay', 'chile']);

const normalize = (text) =>
  String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[.]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// «B1657 San Martín» → «San Martín»; «11300 Montevideo» → «Montevideo»; «X5186» → «».
const stripPostalCode = (segment) =>
  String(segment || '')
    .replace(/^[A-Z]?\d{4,5}[A-Z]{0,3}\b\s*/, '')
    .replace(/\s*\b[A-Z]?\d{4,5}[A-Z]{0,3}$/, '')
    .trim();

function regionIndex(country) {
  const countries = REGIONS[country] ? [country] : Object.keys(REGIONS);
  const index = new Map();
  for (const c of countries) {
    for (const name of REGIONS[c]) index.set(normalize(name), name);
    for (const [alias, name] of Object.entries(ALIASES[c])) index.set(alias, name);
    // «Provincia de Córdoba», «Departamento de Montevideo».
    for (const name of REGIONS[c]) {
      index.set(`provincia de ${normalize(name)}`, name);
      index.set(`departamento de ${normalize(name)}`, name);
    }
  }
  return index;
}

/** Nombre canónico de una provincia/departamento escrito de cualquier forma, o `null`. */
export function canonicalRegion(name, country) {
  return regionIndex(country).get(normalize(stripPostalCode(name))) || null;
}

/**
 * Provincia y ciudad de una dirección «calle 123, Ciudad, Provincia, País». Se recorre de derecha a
 * izquierda (la provincia va al final; así «Av. Corrientes 1234, CABA» no se lee como Corrientes).
 * La ciudad es el tramo anterior a la provincia, si no es la calle (primer tramo o con números).
 */
export function locationFromAddress(address, country) {
  if (!address) return null;
  const index = regionIndex(country);
  const segments = String(address).split(',').map((s) => s.trim()).filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    const clean = stripPostalCode(segments[i]);
    if (COUNTRY_NAMES.has(normalize(clean))) continue;
    const province = index.get(normalize(clean));
    if (!province) continue;
    // Solo el código postal de adelante («B1657 San Martín»): «Arauco 1386» es una calle.
    const citySegment = i > 1 ? segments[i - 1].replace(/^[A-Z]?\d{4,5}[A-Z]{0,3}\b\s*/, '') : null;
    const city =
      citySegment && !/\d/.test(citySegment) && normalize(citySegment) !== normalize(province)
        ? citySegment
        : null;
    return { province, city };
  }
  return null;
}

/**
 * Provincia y ciudad de un negocio: la dirección del negocio, después la de sus servicios activos (la
 * que más se repite) y recién al final `vendor.state`. `{ province: null, city: null }` si no hay nada.
 */
export function vendorLocation(vendor) {
  const country = vendor?.country;
  const services = Array.isArray(vendor?.services) ? vendor.services : [];
  const active = services.filter((s) => !s.status || String(s.status).toLowerCase() === 'active');
  const candidates = [vendor?.address, ...(active.length ? active : services).map((s) => s.address)];

  const counts = new Map();
  for (const address of candidates) {
    const location = locationFromAddress(address, country);
    if (!location) continue;
    const key = `${location.province}|${location.city || ''}`;
    const entry = counts.get(key) || { ...location, n: 0 };
    entry.n += 1;
    counts.set(key, entry);
  }
  // La dirección del negocio (primera candidata) desempata.
  const best = [...counts.values()].sort((a, b) => b.n - a.n)[0];
  if (best) return { province: best.province, city: best.city };

  const fromState = canonicalRegion(vendor?.state, country);
  return { province: fromState || vendor?.state || null, city: null };
}

/** «Alta Gracia, Córdoba» / «Córdoba» / null. */
export function vendorLocationLabel({ province, city } = {}) {
  if (province === CABA) return city ? `${city}, CABA` : 'CABA';
  return [city, province].filter(Boolean).join(', ') || null;
}
